import { useDepartmentText } from 'model/DepartmentTerminology';
import { useForm } from '@mantine/form';
import useLoadingAction from 'hooks/use-loading-action';
import { noop } from 'lodash';
import FormWrapper from 'components/forms/FormWrapper';
import { IconCheck } from '@tabler/icons-react';
import { Box, Checkbox, Collapse, Grid, NumberInput, Select, SimpleGrid, TextInput } from '@mantine/core';
import { FormProps } from 'components/forms/FormProps';
import FormInputGroup from 'components/forms/FormInputGroup';
import { DeviceTypeMultiSelect } from 'components/selects/DeviceTypeSelect';
import { useParams } from 'react-router-dom';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useApi } from 'api/api-context';
import { useMemo } from 'react';
import Toast from 'components/Toast';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import { HrTermTypeMultiSelect } from 'components/selects/hr/HrSelects';
import HrNotificationCalendarPreview, {
  HrTermDueDateLoader,
} from 'components/forms/notification-settings/HrNotificationCalendarPreview';
import { UserPreviewHrNotificationEmailPayload } from 'api/actions/user-preview-hr-notification-email/user-preview-hr-notification-email-payload';
import { UserPreviewHrNotificationEmailResponse } from 'api/actions/user-preview-hr-notification-email/user-preview-hr-notification-email-response';

export interface NotificationSettingsFormData {
  revisions: boolean;
  revisionPlans: boolean;
  departmentIds?: string[];
  deviceTypeIds?: string[];
  hrAvailable: boolean;
  hrTermNotifications: boolean;
  hrTermNotificationLeadDays: number;
  hrTermNotificationPeriod: string;
  hrTermNotificationTime: string;
  hrTermNotificationWeekday: number;
  hrTermNotificationMonthDay: number;
  hrTermNotificationWindowType: string;
  hrTermNotificationWindowValue: number;
  hrTermNotificationUpcomingRepetition: string;
  hrTermNotificationOverdueMode: string;
  hrDepartmentIds: string[];
  hrTermTypeIds: string[];
  hrAvailableDepartmentIds: number[];
  hrAvailableTermTypeIds: number[];
}

interface NotificationSettingsFormProps extends FormProps<NotificationSettingsFormData> {
  revisionsAvailable?: boolean;
  loadHrTermDueDates?: HrTermDueDateLoader;
  loadHrEmailPreview?: (
    request: UserPreviewHrNotificationEmailPayload
  ) => Promise<UserPreviewHrNotificationEmailResponse>;
}

/**
 * Form for editing user's notification settings.
 */
export default function NotificationSettingsForm({
  revisionsAvailable = false,
  loadHrTermDueDates,
  loadHrEmailPreview,
  initialValues = {
    revisions: false,
    revisionPlans: false,
    hrAvailable: false,
    hrTermNotifications: false,
    hrTermNotificationLeadDays: 7,
    hrTermNotificationPeriod: 'daily',
    hrTermNotificationTime: '06:00',
    hrTermNotificationWeekday: 1,
    hrTermNotificationMonthDay: 0,
    hrTermNotificationWindowType: 'days',
    hrTermNotificationWindowValue: 7,
    hrTermNotificationUpcomingRepetition: 'once',
    hrTermNotificationOverdueMode: 'once',
    hrDepartmentIds: [],
    hrTermTypeIds: [],
    hrAvailableDepartmentIds: [],
    hrAvailableTermTypeIds: [],
  },
  onSubmit = noop,
}: NotificationSettingsFormProps) {
  const departmentText = useDepartmentText();
  const { hasPermissionAnywhere } = useApi();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const { userId } = useParams();
  const { isTablet } = useResponsiveDesign();

  const seesDevicesModule = useMemo(() => hasPermissionAnywhere('*-devices'), [hasPermissionAnywhere]);
  const seesRevisionModule = useMemo(
    () => revisionsAvailable && hasPermissionAnywhere('*-revisions'),
    [hasPermissionAnywhere, revisionsAvailable]
  );
  const seesAnyModule = seesDevicesModule || seesRevisionModule || initialValues.hrAvailable;
  const availableHrDepartmentIds = useMemo(
    () => new Set(initialValues.hrAvailableDepartmentIds.map(String)),
    [initialValues.hrAvailableDepartmentIds]
  );
  const availableHrTermTypeIds = useMemo(
    () => new Set(initialValues.hrAvailableTermTypeIds.map(String)),
    [initialValues.hrAvailableTermTypeIds]
  );
  const form = useForm({
    initialValues,
    validate: {
      hrTermNotificationTime: (value) => (/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value) ? null : 'Zadajte platný čas.'),
      hrTermNotificationWindowValue: (value, values) =>
        values.hrTermNotificationWindowType === 'days' && (value < 1 || value > 365)
          ? 'Zadajte hodnotu od 1 do 365 dní.'
          : null,
    },
  });
  const effectiveHrDepartmentIds = useMemo(
    () =>
      (form.values.hrDepartmentIds.length > 0
        ? form.values.hrDepartmentIds
        : initialValues.hrAvailableDepartmentIds.map(String)
      ).map(Number),
    [form.values.hrDepartmentIds, initialValues.hrAvailableDepartmentIds]
  );
  const effectiveHrTermTypeIds = useMemo(
    () =>
      (form.values.hrTermTypeIds.length > 0
        ? form.values.hrTermTypeIds
        : initialValues.hrAvailableTermTypeIds.map(String)
      ).map(Number),
    [form.values.hrTermTypeIds, initialValues.hrAvailableTermTypeIds]
  );

  useDirtyFormHandler(form);

  return (
    <FormWrapper
      pt={24}
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText="Uložiť"
      primaryIcon={<IconCheck stroke="1.5" size={24} />}
      skipSecondaryButtonConfirm={!form.isDirty()}
      hideFooter={!seesAnyModule}
    >
      {seesRevisionModule && (
        <FormInputGroup
          groupTitle="Revízne správy"
          groupTooltip="Upozornenia o zmene stavu revíznej správy, ak sa vás táto zmena týka."
        >
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <Checkbox
              label="Povoliť odosielať upozorenia na revízne správy"
              size="lg"
              name="objekto-revisions"
              {...form.getInputProps('revisions', { type: 'checkbox' })}
            />
          </SimpleGrid>
        </FormInputGroup>
      )}

      {seesDevicesModule && (
        <FormInputGroup
          groupTitle="Sledovanie termínov"
          groupTooltip={departmentText(
            'Upozornenia o blížiacom sa termíne zariadenia. Toto nastavenia môžete obmedziť na niektoré položky úrovne a typy zariadení.',
            'Upozornenia o blížiacom sa termíne zariadenia. Toto nastavenia môžete obmedziť na niektoré strediská a typy zariadení.'
          )}
        >
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <Checkbox
              label="Povoliť odosielať upozorenia na termíny"
              size="lg"
              name="objekto-revisionPlans"
              {...form.getInputProps('revisionPlans', { type: 'checkbox' })}
            />
          </SimpleGrid>

          <Collapse in={form.values.revisionPlans}>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <DepartmentSelect
                multiple
                followedOnly
                confirmSelection
                label={departmentText('Položky úrovne', 'Strediská')}
                size="lg"
                placeholder={departmentText('Všetky položky úrovne', 'Všetky strediská')}
                groupByOrganization
                userId={userId ? Number(userId) : -1}
                name="objekto-departmentIds"
                {...form.getInputProps('departmentIds')}
              />
              <DeviceTypeMultiSelect
                label="Typy zariadení"
                size="lg"
                placeholder="Všetky typy zariadení"
                name="objekto-deviceTypeIds"
                {...form.getInputProps('deviceTypeIds')}
              />
            </SimpleGrid>
          </Collapse>
        </FormInputGroup>
      )}

      {form.values.hrAvailable && (
        <FormInputGroup
          groupTitle="Termíny ľudských zdrojov"
          groupTooltip="Súhrnný e-mail pre oprávnené blížiace sa a omeškané HR termíny."
        >
          <SimpleGrid cols={isTablet ? 1 : 2} spacing={isTablet ? 20 : 40}>
            <Checkbox
              label="Povoliť upozornenia na HR termíny"
              size="lg"
              name="objekto-hrTermNotifications"
              {...form.getInputProps('hrTermNotifications', { type: 'checkbox' })}
            />
          </SimpleGrid>

          <Collapse in={form.values.hrTermNotifications}>
            <Grid columns={isTablet ? 1 : 3} gutter={isTablet ? 20 : 40}>
              <Grid.Col span={isTablet ? 1 : 2}>
                <SimpleGrid cols={isTablet ? 1 : 2} spacing={isTablet ? 20 : 40}>
                  <Select
                    label="Perióda odosielania"
                    size="lg"
                    withAsterisk
                    name="objekto-hrTermNotificationPeriod"
                    data={[
                      { value: 'daily', label: 'Denne' },
                      { value: 'weekly', label: 'Týždenne' },
                      { value: 'monthly', label: 'Mesačne' },
                    ]}
                    {...form.getInputProps('hrTermNotificationPeriod')}
                  />
                  <TextInput
                    type="time"
                    label="Čas odoslania"
                    size="lg"
                    withAsterisk
                    name="objekto-hrTermNotificationTime"
                    {...form.getInputProps('hrTermNotificationTime')}
                  />
                  {form.values.hrTermNotificationPeriod === 'weekly' && (
                    <Select
                      label="Deň v týždni"
                      size="lg"
                      withAsterisk
                      value={String(form.values.hrTermNotificationWeekday)}
                      onChange={(value) => form.setFieldValue('hrTermNotificationWeekday', Number(value ?? 1))}
                      data={[
                        { value: '1', label: 'Pondelok' },
                        { value: '2', label: 'Utorok' },
                        { value: '3', label: 'Streda' },
                        { value: '4', label: 'Štvrtok' },
                        { value: '5', label: 'Piatok' },
                        { value: '6', label: 'Sobota' },
                        { value: '7', label: 'Nedeľa' },
                      ]}
                    />
                  )}
                  {form.values.hrTermNotificationPeriod === 'monthly' && (
                    <Select
                      label="Deň v mesiaci"
                      size="lg"
                      withAsterisk
                      value={String(form.values.hrTermNotificationMonthDay)}
                      onChange={(value) => form.setFieldValue('hrTermNotificationMonthDay', Number(value ?? 0))}
                      data={[
                        { value: '0', label: 'Posledný deň mesiaca' },
                        ...Array.from({ length: 31 }, (_, index) => ({
                          value: String(index + 1),
                          label: `${index + 1}. deň mesiaca`,
                        })),
                      ]}
                    />
                  )}
                  <Select
                    label="Sledované obdobie"
                    size="lg"
                    withAsterisk
                    name="objekto-hrTermNotificationWindowType"
                    data={[
                      { value: 'today', label: 'Dnes' },
                      { value: 'current-week', label: 'Aktuálny týždeň' },
                      { value: 'current-month', label: 'Aktuálny mesiac' },
                      { value: 'days', label: 'Nasledujúci počet dní' },
                      { value: 'next-week', label: 'Nasledujúci týždeň' },
                      { value: 'next-month', label: 'Nasledujúci mesiac' },
                    ]}
                    {...form.getInputProps('hrTermNotificationWindowType')}
                  />
                  {form.values.hrTermNotificationWindowType === 'days' && (
                    <NumberInput
                      label="Počet sledovaných dní"
                      size="lg"
                      min={1}
                      max={365}
                      withAsterisk
                      name="objekto-hrTermNotificationWindowValue"
                      {...form.getInputProps('hrTermNotificationWindowValue')}
                    />
                  )}
                  <Select
                    label="Opakovanie blížiacich sa termínov"
                    size="lg"
                    withAsterisk
                    name="objekto-hrTermNotificationUpcomingRepetition"
                    data={[
                      { value: 'once', label: 'Upozorniť raz' },
                      {
                        value: 'every-run',
                        label: 'Zahrnúť v každom súhrne, kým je termín v sledovanom období',
                      },
                    ]}
                    {...form.getInputProps('hrTermNotificationUpcomingRepetition')}
                  />
                  <Select
                    label="Omeškané termíny"
                    size="lg"
                    withAsterisk
                    name="objekto-hrTermNotificationOverdueMode"
                    data={[
                      { value: 'off', label: 'Neupozorňovať' },
                      { value: 'once', label: 'Upozorniť raz po omeškaní' },
                      { value: 'every-run', label: 'Opakovať v každom súhrne až do vyriešenia' },
                    ]}
                    {...form.getInputProps('hrTermNotificationOverdueMode')}
                  />
                  <DepartmentSelect
                    multiple
                    confirmSelection
                    label={departmentText('Položky úrovne', 'Strediská')}
                    size="lg"
                    placeholder={departmentText('Všetky dostupné položky úrovne', 'Všetky dostupné strediská')}
                    groupByOrganization
                    name="objekto-hrDepartmentIds"
                    filterData={(departments) =>
                      departments.filter((department) => availableHrDepartmentIds.has(String(department.value)))
                    }
                    {...form.getInputProps('hrDepartmentIds')}
                  />
                  <HrTermTypeMultiSelect
                    label="Typy termínov"
                    size="lg"
                    placeholder="Všetky dostupné typy"
                    name="objekto-hrTermTypeIds"
                    filterData={(termTypes) =>
                      termTypes.filter((termType) => availableHrTermTypeIds.has(String(termType.value)))
                    }
                    {...form.getInputProps('hrTermTypeIds')}
                  />
                </SimpleGrid>
              </Grid.Col>
              <Grid.Col span={1}>
                <Box mt={isTablet ? 28 : 0}>
                  <HrNotificationCalendarPreview
                    period={form.values.hrTermNotificationPeriod}
                    weekday={form.values.hrTermNotificationWeekday}
                    monthDay={form.values.hrTermNotificationMonthDay}
                    windowType={form.values.hrTermNotificationWindowType}
                    windowValue={form.values.hrTermNotificationWindowValue}
                    departmentIds={effectiveHrDepartmentIds}
                    termTypeIds={effectiveHrTermTypeIds}
                    loadHrTermDueDates={loadHrTermDueDates}
                    loadHrEmailPreview={
                      loadHrEmailPreview
                        ? (evaluationDate) =>
                            loadHrEmailPreview({
                              evaluationDate,
                              period: form.values.hrTermNotificationPeriod,
                              time: form.values.hrTermNotificationTime,
                              weekday: form.values.hrTermNotificationWeekday,
                              monthDay: form.values.hrTermNotificationMonthDay,
                              windowType: form.values.hrTermNotificationWindowType,
                              windowValue: form.values.hrTermNotificationWindowValue,
                              upcomingRepetition: form.values.hrTermNotificationUpcomingRepetition,
                              overdueMode: form.values.hrTermNotificationOverdueMode,
                              departmentIds: form.values.hrDepartmentIds.map(Number),
                              termTypeIds: form.values.hrTermTypeIds.map(Number),
                            })
                        : undefined
                    }
                  />
                </Box>
              </Grid.Col>
            </Grid>
          </Collapse>
        </FormInputGroup>
      )}

      {!seesAnyModule && (
        <Box px={isTablet ? 20 : 40} pb={8}>
          <Toast
            type="info"
            message="Váš prístup nepodporuje žiadne upozornenia"
            fullWidth
            opened
            withCloseButton={false}
          />
        </Box>
      )}
    </FormWrapper>
  );
}
